import { useTranslation } from 'react-i18next';

import {
  Form,
  TextInput,
  PLATFORM_THEME_COLORS,
  NumberInput,
  SelectInput,
  Loader,
} from '@thinknet-gmbh/platform-common-ui';

import { EmployeeStatusesEnum } from '@thinknet-gmbh/platform-common';

import { BooleanSelectInput } from 'src/pages/employee/components/boolean-select-input';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetPermissionsGroups } from 'src/hooks/permission-group/get-permissions-groups.hook';
import { useGetCurrentSubscription } from 'src/hooks/subscriptions/get-current-subscription.hook';

import { PersonalInfoRow } from '../../personal-info.styles';
import { TitleRowLabel, StyledContainer } from '../../../employee-common.styles';

const SystemData = ({
  isEdit,
  employeeId,
  memberId,
  isOwnData,
}: {
  isEdit?: boolean;
  isOwnData?: boolean;
  employeeId?: string;
  memberId?: string;
}) => {
  const { t } = useTranslation(['common', 'employee']);

  const currentCompanyId = useGetCurrentCompanyId();
  const { permissionsGroups, loading } = useGetPermissionsGroups(currentCompanyId);
  const { subscription } = useGetCurrentSubscription();

  if (loading) {
    return <Loader />;
  }

  return (
    <StyledContainer>
      <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
        {t('employee:labels.systemData')}
      </TitleRowLabel>

      <PersonalInfoRow>
        <Form.Item
          label={t('employee:labels.internalNumber')}
          rules={[
            { required: true, message: t('employee:validationError.internalNumberRequired') },
          ]}
          initialValue={0}
        >
          <NumberInput
            disabled
            placeholder={t('employee:labels.internalNumberPlaceholder')}
            customInput={TextInput}
          />
        </Form.Item>
        <Form.Item shouldUpdate>
          {() => (
            <Form.Item
              name={['employeePersonalInfo', 'employeeNumber']}
              label={t('employee:labels.employeeNumber')}
              rules={[
                { required: true, message: t('employee:validationError.employeeNumberRequired') },
                { type: 'number', message: t('common:validation.error.mustBeInteger') },
                {
                  validator: (_, value) =>
                    Number.isInteger(value)
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('common:validation.error.mustBeInteger'))),
                },
              ]}
            >
              <NumberInput
                maxLength={5}
                placeholder="44423"
                allowLeadingZeros={false}
                thousandSeparator={false}
                customInput={TextInput}
                disabled={isOwnData}
              />
            </Form.Item>
          )}
        </Form.Item>
        <Form.Item
          name={['employeePersonalInfo', 'status']}
          label={t('common:labels.status')}
          rules={[{ required: true, message: t('common:validation.error.statusRequired') }]}
          initialValue={EmployeeStatusesEnum.INACTIVE}
        >
          <SelectInput placeholder="Active" disabled={isOwnData}>
            {Object.keys(EmployeeStatusesEnum).map(status => {
              const disabledOption =
                (!subscription.allowedEmployeesAddition &&
                  EmployeeStatusesEnum.ACTIVE === status) ||
                (status === EmployeeStatusesEnum.INACTIVE && isEdit && employeeId === memberId);

              return (
                <SelectInput.Option
                  value={status}
                  key={status}
                  disabled={disabledOption}
                  {...(disabledOption && {
                    title: t('common:labels.subscriptionPlacesAlert'),
                  })}
                >
                  {t(`employee:employeePersonalInfo.labels.statuses.${status.toLowerCase()}`)}
                </SelectInput.Option>
              );
            })}
          </SelectInput>
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const isAccess = getFieldValue(['employeePersonalInfo', 'accessToPlatform']);

            return (
              <Form.Item
                name={'permissionGroupId'}
                label={t('employee:labels.permissionType')}
                rules={[
                  {
                    required: isAccess,
                    message: t('employee:validationError.permissionTypeRequired'),
                  },
                ]}
              >
                <SelectInput disabled={!isAccess || isOwnData} placeholder="HR">
                  {permissionsGroups.map((permissionGroup: any) => (
                    <SelectInput.Option key={permissionGroup._id} value={permissionGroup._id}>
                      {permissionGroup.name}
                    </SelectInput.Option>
                  ))}
                </SelectInput>
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item
          name={['employeePersonalInfo', 'accessToPlatform']}
          label={t('employee:employeePersonalInfo.labels.accessLabel')}
          rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
        >
          <BooleanSelectInput disabled={isOwnData}  t={t} />
        </Form.Item>
      </PersonalInfoRow>
    </StyledContainer>
  );
};

export default SystemData;
